import { HANDSHAKE_REQUIRED_STATUS } from '@breetometer/utils/handshake';
import {
	type BaseQueryApi,
	createApi,
	type FetchArgs,
	fetchBaseQuery,
	type FetchBaseQueryError,
	type FetchBaseQueryMeta,
	type QueryReturnValue,
} from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { baseApiHeaders, getLocationFrom, HIDE_TOAST_KEY, type IApiResponse, showToast } from 'BreetHelpers';

const baseQuery = fetchBaseQuery({
	baseUrl: `${typeof window !== 'undefined' ? window.location.origin : ''}/proxy`,
	headers: baseApiHeaders,
	credentials: 'include',
});

const secureBaseQuery = fetchBaseQuery({
	baseUrl: `${typeof window !== 'undefined' ? window.location.origin : ''}/api/secure`,
	headers: baseApiHeaders,
	credentials: 'include',
});

const handleToastNotification = (
	result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>,
	isGetRequest: boolean
) => {
	const successResp = result.data as IApiResponse | undefined;
	const errorResp = result.error?.data as IApiResponse | undefined;
	const isToastHidden = !!result.meta?.request.headers.get(HIDE_TOAST_KEY);

	if (!isToastHidden && !isGetRequest) {
		showToast(successResp?.message ? 'success' : 'error', successResp?.message ?? errorResp?.message ?? 'Something went wrong');
	}
};

const handleServerError = (result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>) => {
	const errorReqStatusCode = typeof result.error?.status === 'number' && result.error.status;

	if (errorReqStatusCode && errorReqStatusCode >= 500) {
		showToast('error', 'Internal server error. Please be patient 🙏');
		return true;
	}
	return false;
};

const mutex = new Mutex();

const baseQueryWithReauth = async (args: string | FetchArgs, api: BaseQueryApi, extraOptions: object) => {
	await mutex.waitForUnlock();

	let result = await baseQuery(args, api, extraOptions);

	// If backend requires handshake (419), retry through secure proxy
	if (result.error?.status === HANDSHAKE_REQUIRED_STATUS) {
		result = await secureBaseQuery(args, api, extraOptions);
	}

	if (handleServerError(result)) {
		return result;
	}

	handleToastNotification(result, result.meta?.request.method === 'GET');

	result = await attemptTokenRefresh(api, extraOptions, args, result);

	if (!result.error && result.data && result.meta?.response?.status) {
		result = {
			data: {
				...(result.data as object),
				statusCode: result.meta.response.status,
			},
			meta: result.meta,
		};
	}

	return result;
};

const attemptTokenRefresh = async (
	api: BaseQueryApi,
	extraOptions: object,
	args: string | FetchArgs,
	resultArg: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>
) => {
	let result = resultArg;
	if (result.error?.status === 401) {
		if (result.meta?.response?.url.includes('kyb')) {
			api.dispatch({
				payload: undefined,
				type: 'kybAuth/logKybUserOut',
			});
		}

		if (!mutex.isLocked()) {
			const release = await mutex.acquire();

			try {
				const refreshResult = await baseQuery('/auth/refresh', api, extraOptions);
				if (refreshResult.data) {
					result = await baseQuery(args, api, extraOptions);
				} else {
					throw new Error();
				}
			} catch (_e: unknown) {
				api.dispatch({
					payload: undefined,
					type: 'app/logUserOut',
				});

				const locationFrom = getLocationFrom();
				const { default: router } = await import('../../../router');

				router.navigate('/getlost', { state: { locationFrom }, replace: true }).catch(console.error);
				console.error(_e);
			} finally {
				release();
			}
		} else {
			await mutex.waitForUnlock();
			result = await baseQuery(args, api, extraOptions);
		}
	}

	return result;
};

export const apiSlice = createApi({
	reducerPath: 'api',
	baseQuery: baseQueryWithReauth,
	endpoints: () => ({}),
	tagTypes: [
		'Kyb',
		'User',
		'GetBanks',
		'GetBeneficiaries',
		'GetUserWallets',
		'GetUserPreference',
		'GenerateOTP',
		'GetSellAssets',
		'GetNotifications',
		'AllTransactions',
		'UserRewardPoints',
		'GetSavedWalletAddresses',
		'GetDeveloperIntegration',
		'GetTradesSubAccountWalletAddresses',
		'GetWebhookLogs',
		'GetWebhookLogById',
	],
});
